import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import UserProfileIcon from '../components/UserProfileIcon';
import Logo from '../components/Logo';
import {
  IELTSExamType,
  IELTSExamDuration,
  IELTS_EXAM_CONFIGS,
  IELTSDurationConfig,
} from '../types/ielts';
import { IELTSExamType as SubscriptionIELTSExamType } from '../types/subscription';

const IELTSPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const {
    canUseIeltsExam,
    canUseFullIelts,
    trackBetaIeltsUse,
    subscription
  } = useSubscription();
  const [selectedExamType, setSelectedExamType] = useState<IELTSExamType | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<IELTSExamDuration | null>(null);

  // Check if user has unlimited access (pro users)
  const hasUnlimitedAccess = canUseFullIelts();
  const examsUsed = subscription?.betaIeltsExamsUsed || [];

  // Check if user can use a specific exam type
  const canUserUseExam = (examType: IELTSExamType): boolean => {
    return canUseIeltsExam(examType as SubscriptionIELTSExamType);
  };

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const selectedExamConfig = IELTS_EXAM_CONFIGS.find(
    (config) => config.type === selectedExamType
  );

  const handleStartExam = async () => {
    if (selectedExamType && selectedDuration) {
      // Check if user can use this exam
      if (!canUserUseExam(selectedExamType)) {
        return;
      }

      // Track usage for non-pro users before navigating
      if (!hasUnlimitedAccess) {
        await trackBetaIeltsUse(selectedExamType as SubscriptionIELTSExamType);
      }

      navigate(`/app/ielts/${selectedExamType}`, {
        state: { duration: selectedDuration },
      });
    }
  };

  const getExamIcon = (type: IELTSExamType) => {
    switch (type) {
      case 'speaking':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
          </svg>
        );
      case 'writing':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        );
      case 'listening':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 001.414 1.414m2.828-9.9a9 9 0 0112.728 0" />
          </svg>
        );
      case 'reading':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm dark:shadow-gray-900">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <Logo size="md" onClick={() => navigate('/app')} />
            <span className="text-sm text-gray-600 dark:text-gray-400 hidden sm:block">
              Welcome, <span className="font-medium">{currentUser?.email}</span>
            </span>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={handleLogout}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center gap-1 p-2 -m-2"
              title="Logout"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="hidden sm:inline">Logout</span>
            </button>
            <UserProfileIcon />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">IELTS Exam Practice</h1>
          <p className="text-gray-600 dark:text-gray-300">
            Experience realistic IELTS exam conditions with AI-powered feedback
          </p>
        </div>

        {/* Trial Access Info Banner - shown for non-pro users */}
        {!hasUnlimitedAccess && (
          <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 rounded-xl p-5 mb-8">
            <div className="flex items-start gap-4">
              <span className="text-2xl">🎯</span>
              <div className="flex-1">
                <h3 className="font-semibold text-emerald-800 dark:text-emerald-200 mb-2">Trial Access</h3>
                <p className="text-sm text-emerald-700 dark:text-emerald-300 mb-3">
                  You can try each IELTS exam type once (short version only).
                  Upgrade to Pro for unlimited access to all durations.
                </p>
                <div className="flex flex-wrap gap-3">
                  {(['speaking', 'writing', 'reading', 'listening'] as IELTSExamType[]).map((exam) => {
                    const isUsed = examsUsed.includes(exam);
                    return (
                      <span
                        key={exam}
                        className={`text-xs px-3 py-1 rounded-full font-medium ${
                          isUsed
                            ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 line-through'
                            : 'bg-emerald-200 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-200'
                        }`}
                      >
                        {exam.charAt(0).toUpperCase() + exam.slice(1)}: {isUsed ? 'Used' : 'Available'}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Exam Type Selection */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-4">Select Exam Type</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {IELTS_EXAM_CONFIGS.map((config) => {
              const isExamDisabled = !canUserUseExam(config.type);

              return (
                <button
                  key={config.type}
                  onClick={() => {
                    if (!isExamDisabled) {
                      setSelectedExamType(config.type);
                      // For non-pro users, auto-select short duration
                      setSelectedDuration(!hasUnlimitedAccess ? 'short' : null);
                    }
                  }}
                  disabled={isExamDisabled}
                  className={`p-6 rounded-xl border-2 transition-all text-center ${
                    isExamDisabled
                      ? 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 opacity-60 cursor-not-allowed'
                      : selectedExamType === config.type
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 shadow-md'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-emerald-300 dark:hover:border-emerald-600 hover:shadow-sm hover:scale-105 transform'
                  }`}
                >
                  <div
                    className={`mx-auto mb-3 ${
                      isExamDisabled
                        ? 'text-gray-400 dark:text-gray-500'
                        : selectedExamType === config.type
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {getExamIcon(config.type)}
                  </div>
                  <h3
                    className={`font-semibold ${
                      isExamDisabled
                        ? 'text-gray-400 dark:text-gray-500'
                        : selectedExamType === config.type
                          ? 'text-emerald-700 dark:text-emerald-300'
                          : 'text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {config.label}
                    {config.type === 'listening' && (
                      <span className="ml-2 text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 rounded-full font-medium">
                        Beta
                      </span>
                    )}
                  </h3>
                  <p className={`text-xs mt-1 ${isExamDisabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'}`}>
                    {isExamDisabled ? 'Already used (trial limit)' : config.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Duration Selection */}
        {selectedExamConfig && (
          <div className="mb-8 animate-fadeIn">
            <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-4">Select Duration</h2>
            {!hasUnlimitedAccess && (
              <p className="text-sm text-emerald-600 dark:text-emerald-400 mb-3 italic">
                Trial users can only use Quick mode. Upgrade to Pro for full access.
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {selectedExamConfig.durations.map((duration: IELTSDurationConfig) => {
                // Non-pro users can only use short duration
                const isTrialLocked = !hasUnlimitedAccess && duration.duration !== 'short';
                const isListeningDisabled = selectedExamType === 'listening' && (duration.duration === 'medium' || duration.duration === 'full');
                const isDisabled = isTrialLocked || isListeningDisabled;

                return (
                  <button
                    key={duration.duration}
                    onClick={() => !isDisabled && setSelectedDuration(duration.duration)}
                    disabled={isDisabled}
                    className={`p-6 rounded-xl border-2 transition-all text-left ${
                      isDisabled
                        ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 cursor-not-allowed opacity-60'
                        : selectedDuration === duration.duration
                          ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-emerald-300 dark:hover:border-emerald-600'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3
                        className={`font-semibold ${
                          isDisabled
                            ? 'text-gray-400 dark:text-gray-500'
                            : selectedDuration === duration.duration
                              ? 'text-emerald-700 dark:text-emerald-300'
                              : 'text-gray-700 dark:text-gray-200'
                        }`}
                      >
                        {duration.label}
                        {isTrialLocked && (
                          <span className="ml-2 text-xs px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300 rounded font-normal">
                            PRO
                          </span>
                        )}
                      </h3>
                      {isListeningDisabled ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          Coming Soon
                        </span>
                      ) : (
                        <span
                          className={`text-sm px-2 py-1 rounded-full ${
                            isDisabled
                              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                              : selectedDuration === duration.duration
                                ? 'bg-emerald-200 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-200'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                          }`}
                        >
                          ~{duration.timeMinutes} min
                        </span>
                      )}
                    </div>
                    <p className={`text-sm ${isDisabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'}`}>{duration.description}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Start Button */}
        {selectedExamType && selectedDuration && (
          <div className="text-center animate-fadeIn">
            <button
              onClick={handleStartExam}
              className="px-8 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-colors flex items-center gap-2 mx-auto"
            >
              Start {selectedExamConfig?.label} Exam
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        )}

        {/* IELTS Info */}
        <div className="mt-12 bg-gradient-to-br from-white to-emerald-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl shadow-lg dark:shadow-gray-900 p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">About IELTS Practice</h3>
            <p className="text-gray-600 dark:text-gray-300">Master all four components of the IELTS exam with AI-powered feedback</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Speaking */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm dark:shadow-gray-900 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </div>
              <h4 className="font-semibold text-gray-800 dark:text-white mb-3">Speaking Test</h4>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">•</span>
                  Part 1: Introduction (4-5 min)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">•</span>
                  Part 2: Topic Card (3-4 min)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">•</span>
                  Part 3: Discussion (4-5 min)
                </li>
              </ul>
              <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
                <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">AI evaluates fluency, vocabulary, grammar & pronunciation</span>
              </div>
            </div>

            {/* Listening */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm dark:shadow-gray-900 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 001.414 1.414m2.828-9.9a9 9 0 0112.728 0" />
                </svg>
              </div>
              <h4 className="font-semibold text-gray-800 dark:text-white mb-3">Listening Test</h4>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">•</span>
                  4 sections, 40 questions
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">•</span>
                  Conversations & monologues
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">•</span>
                  ~30 minutes + transfer time
                </li>
              </ul>
              <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
                <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">AI-generated audio with realistic accents</span>
              </div>
            </div>

            {/* Writing */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm dark:shadow-gray-900 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900/50 flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
              <h4 className="font-semibold text-gray-800 dark:text-white mb-3">Writing Test</h4>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">•</span>
                  Task 1: over 150 words (20 min)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">•</span>
                  Task 2: over 250 words (40 min)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">•</span>
                  Academic & General Training
                </li>
              </ul>
              <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
                <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">AI evaluates task achievement, coherence & grammar</span>
              </div>
            </div>

            {/* Reading */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm dark:shadow-gray-900 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h4 className="font-semibold text-gray-800 dark:text-white mb-3">Reading Test</h4>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">•</span>
                  3 passages, 40 questions
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">•</span>
                  60 minutes total
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">•</span>
                  Multiple question types
                </li>
              </ul>
              <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
                <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Practice with diverse academic topics</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IELTSPage;
